import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./Modal";
import ModalRemove from "./ModalRemove";
import Multiselect from "./Multiselect";
import { useDispatch, useSelector } from "react-redux";
import {
  ObtenerTodos,
  Actualizar,
  Eliminar,
  Insertar,
  ImportarDocumentos,
} from "../../redux/perfilesDocumentosTiposSlice";
import { obtenerExtensionesArchivos } from "../../redux/genericosSlice";
import { toast } from "react-toastify";
import WindowDimension from "../../Hooks/WindowDimension";
import OperationStatus from "../../config/OperationStatus";
import Required from "../../Components/Required";
import { OrderTable } from "../../Components/OrderTable";
import useSubmit from "../../Hooks/useSubmit";
import { getAdminDocumentTypePage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { StrapiError } from '../../Inicio/Pages/StrapiError';
import { _translateValue } from "../translation-gestor/translateValue";

import { useValidation } from "../../Hooks/useValidation";

const SCREEN_NAME = "PantallaPerfilesDocumentosTipos";
function PerfilesDocumentosTipos(props) {
  const { validation } = useValidation();
  const { locale } = useLocale();
  const [adminDocumentTypePage, setAdminDocumentTypePage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [translatedDocumentos, setTranslatedDocumentos] = useState({});
  const [translatedDescripciones, setTranslatedDescripciones] = useState({});
  const [translatedNombresDescarga, setTranslatedNombresDescarga] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tituloModal, setTituloModal] = useState("Agregar");
  const [removeState, changeRemoveState] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const { width } = WindowDimension();

  const dispatch = useDispatch();
  let busqueda = useRef();
  const multiSelectRef = useRef(null);

  const { perfilesDocumentosTipos } = useSelector(
    (state) => state.perfilesDocumentosTipos
  );
  const { archivosTipos } = useSelector((state) => state.genericos);

  const { submitAction } = useSubmit();

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    reset,
    setValue,
  } = useForm();

  const columns = useMemo(() => {
    const table = adminDocumentTypePage?.documenttype_table ?? [];
    return [
      { label: table[0]?.label || "Código", accessor: "documentoId", sortable: true },
      { 
        label: table[1]?.label || "Documento", 
        accessor: "documentoNombre", 
        sortable: true,
        render: (item) => (
          <td>{translatedDocumentos[item.documentoNombre] || item.documentoNombre}</td>
        ),
      },
      { 
        label: table[2]?.label || "Descripción", 
        accessor: "descripcion", 
        sortable: true,
        render: (item) => (
          <td>{translatedDescripciones[item.descripcion] || item.descripcion}</td>
        ),
      },
      { 
        label: table[3]?.label || "Nombre de la descarga", 
        accessor: "nombreDescarga", 
        sortable: true,
        render: (item) => (
          <td>{translatedNombresDescarga[item.nombreDescarga] || item.nombreDescarga}</td>
        ),
      },
      {
        label: table[4]?.label || "Tipos de archivos",
        accessor: "tipoArchivos",
        sortable: true,
        render: (item) => (
          <td>
            <ul>
              {item.tipoArchivos.map((ar, i) => (
                <li key={i}>{ar.tipoArchivoNombre}</li>
              ))}
            </ul>
          </td>
        ),
      },
      { label: table[5]?.label || "Acción", accessor: "action", sortable: false },
    ];
  }, [adminDocumentTypePage, translatedDocumentos, translatedDescripciones, translatedNombresDescarga]);

  const showModalEdit = (item) => {
    setItemSelected(item);
    reset(item);
    setTituloModal(adminDocumentTypePage?.subtitleO || "Editar");
    setShowModal(true);
  };

  const closeModalEdit = () => {
    setItemSelected([]);
    multiSelectRef.current.resetSelectedValues();
    reset({ id: 0, documentoNombre: "" });
    setShowModal(false);
  };

  const showRemoveModalEdit = (item) => {
    setItemSelected(item);
    changeRemoveState(true);
  };

  const closeRemoveModalEdit = () => {
    setItemSelected({});
    changeRemoveState(false);
  };

  const cargarTiposArchivos = () => {
    dispatch(obtenerExtensionesArchivos());
  };

  const cargarDatos = () => {
    dispatch(ObtenerTodos());
  };

  const cargarDatosPorCaracter = () => {
    dispatch(ObtenerTodos(busqueda.current.value));
  };

  const eliminar = async () => {
    submitAction(Eliminar, itemSelected?.documentoId, () => {
      closeRemoveModalEdit();
      cargarDatos();
    });
  };

  const onSubmit = async (data) => {
    submitAction(data.documentoId > 0 ? Actualizar : Insertar, data, () => {
      closeModalEdit();
      cargarDatos();
    });
  };

  const importar = async () => {
    let { payload } = await dispatch(ImportarDocumentos());
    if (payload.operationStatus === OperationStatus.SUCCESS) {
      cargarDatos();
      toast.success(payload.message);
    } else {
      toast.error(payload.message);
    }
  };

  useEffect(() => {
    cargarDatos();
    cargarTiposArchivos();
  }, [dispatch]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminDocumentTypePage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminDocumentTypePage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminDocumentTypePage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir los campos "Documento", "Descripción" y "Nombre de la descarga" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasPerfilesDocumentosTipos = perfilesDocumentosTipos && Array.isArray(perfilesDocumentosTipos) && perfilesDocumentosTipos.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasPerfilesDocumentosTipos) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const documentoTranslations = {};
      const descripcionTranslations = {};
      const nombreDescargaTranslations = {};

      // Traducir solo los campos solicitados
      const translationPromises = perfilesDocumentosTipos.map(async (item) => {
        if (item.documentoNombre) {
          const translated = await _translateValue(SCREEN_NAME, item.documentoNombre, locale);
          documentoTranslations[item.documentoNombre] = translated;
        }
        if (item.descripcion) {
          const translated = await _translateValue(SCREEN_NAME, item.descripcion, locale);
          descripcionTranslations[item.descripcion] = translated;
        }
        if (item.nombreDescarga) {
          const translated = await _translateValue(SCREEN_NAME, item.nombreDescarga, locale);
          nombreDescargaTranslations[item.nombreDescarga] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedDocumentos(documentoTranslations);
      setTranslatedDescripciones(descripcionTranslations);
      setTranslatedNombresDescarga(nombreDescargaTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [perfilesDocumentosTipos, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <div className="main-admin">
        <div className="main-admin-bar">
          <div className="header-admin-bar-items">
            <Skeleton.Input style={{ width: 300, height: 30 }} active />
          </div>
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
            <Skeleton.Button style={{ width: 40, height: 40 }} active />
          </div>
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </div>
    );
  }

  return (
    <div className="main-admin">
      <div className="main-admin-bar">
        <div className="header-admin-bar-items">
          <h2>{adminDocumentTypePage?.title || "Tipos de documentos"}</h2>
        </div>

        <div className="header-admin-bar-items ml-auto">
          <label hidden>Búsqueda</label>
          <input
            type="search"
            className="form-control"
            placeholder={adminDocumentTypePage?.search_input || "Búsqueda"}
            onInput={cargarDatosPorCaracter}
            ref={busqueda}
          />
          <button className="search-button" onClick={importar}>
            <i className="material-icons"></i>
          </button>
        </div>
      </div>

      <div className="table-container">
        {perfilesDocumentosTipos.length > 0 ? (
          <>
            <OrderTable
              {...{ columns }}
              data={perfilesDocumentosTipos}
              clase="table-adm"
              actions={[
                {
                  icon: "",
                  clase: "text-primary",
                  event: (item) => showModalEdit(item),
                },
                {
                  icon: "",
                  clase: "text-primary",
                  event: (item) => showRemoveModalEdit(item),
                },
              ]}
            />
          </>
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>

      <Modal
        estado={showModal}
        cambiarEstado={closeModalEdit}
        titulo={tituloModal}
        clase={`${width < 576 ? "full-width" : "small"}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(onSubmit)}
            >
              {adminDocumentTypePage?.save_button || "Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div className="form-group">
            <label>{adminDocumentTypePage?.document || "Documento"}</label>
            <Required />
            <input
              className="form-control"
              name="documentoNombre"
              {...register("documentoNombre", { required: validation?.required_field || "Campo requerido" })}
            />
            {errors.documentoNombre && (
              <p className="error">{errors.documentoNombre.message}</p>
            )}
          </div>
          <div className="form-group">
            <label>{adminDocumentTypePage?.download_name || "Nombre de descarga"}</label>
            <Required />
            <input
              className="form-control"
              name="nombreDescarga"
              {...register("nombreDescarga", { required: validation?.required_field || "Campo requerido" })}
            />
            {errors.nombreDescarga && (
              <p className="error">{errors.nombreDescarga.message}</p>
            )}
          </div>
          <div className="form-group">
            <label>{adminDocumentTypePage?.description || "Descripción"}</label>
            <Required />
            <input
              className="form-control"
              name="descripcion"
              {...register("descripcion", { required: validation?.required_field || "Campo requerido" })}
            />
            {errors.descripcion && (
              <p className="error">{errors.descripcion.message}</p>
            )}
          </div>
          <div className="form-group">
            <label>{adminDocumentTypePage?.documenttype || "Tipos de documentos"}</label>
            <Required />
            <Multiselect
              innerRef={multiSelectRef}
              options={archivosTipos}
              selectedValues={itemSelected?.tipoArchivos}
              name="tipoArchivos"
              setValueArray="tipoArchivos"
              displayValue="tipoArchivoNombre"
              control={control}
              setValue={setValue}
              placeholder="Seleccione los tipos de archivos"
              rules={{ required: validation?.required_field || "Campo requerido" }}
            />

            {errors.tipoArchivos && (
              <p className="error">{errors.tipoArchivos.message}</p>
            )}
          </div>
        </form>
      </Modal>

      <ModalRemove
        estado={removeState}
        closeModalHandled={closeRemoveModalEdit}
        title={adminDocumentTypePage?.subtitleT || "Eliminar plantillla"}
        message={adminDocumentTypePage?.question || "¿Seguro que desea eliminar el siguiente registro?"}
        item={itemSelected?.documentoNombre}
        btnGroup={
          <>
            <button className="button-text text-red" onClick={eliminar}>
              {adminDocumentTypePage?.confirm_button || "Confirmar"}
            </button>
            <button
              className="button-text text-grey"
              onClick={() => {
                closeRemoveModalEdit(false);
              }}
            >
              {adminDocumentTypePage?.cancel_button || "Cancelar"}
            </button>
          </>
        }
      />
    </div>
  );
}

export default PerfilesDocumentosTipos;
